import { createFileRoute, Link } from '@tanstack/react-router';
import { PublicHeader } from '@/components/public/public-header';
import { PublicFooter } from '@/components/public/public-footer';
import { useRouteMeta } from '@/lib/use-route-meta';

/**
 * Página pública pra terapeutas conhecerem a plataforma. Não está em nenhum
 * menu — é compartilhada via link direto. Para incluir no header futuramente,
 * adicionar `/para-terapeutas` no `public-header.tsx`.
 */
export const Route = createFileRoute('/para-terapeutas')({ component: ParaTerapeutasPage });

const PLANS = [
  {
    tier: 'FREE',
    name: 'Gratuito',
    price: 'R$ 0',
    feePct: 15,
    highlights: [
      'Listagem na busca pública',
      'Agendamento + Google Meet automático',
      'Cobrança por cartão e PIX',
      'Prontuário digital + transcrição',
      'Sem limite de sessões',
    ],
    badge: null,
    accent: 'slate',
  },
  {
    tier: 'PRO',
    name: 'Pro',
    price: 'R$ 49',
    feePct: 10,
    highlights: [
      'Tudo do plano Gratuito',
      'Badge "PRO" no perfil público',
      'Destaque nos resultados de busca',
      'Taxa reduzida em 5 p.p.',
      'Relatórios avançados',
    ],
    badge: 'Mais escolhido',
    accent: 'teal',
  },
  {
    tier: 'PREMIUM',
    name: 'Premium',
    price: 'R$ 99',
    feePct: 7,
    highlights: [
      'Tudo do plano Pro',
      'Badge "PREMIUM" dourado',
      'Posição prioritária no topo da busca',
      'Taxa mínima do mercado',
      'Suporte prioritário',
    ],
    badge: 'Para quem cresce',
    accent: 'coral',
  },
];

const FLOW_STEPS = [
  { num: '1', title: 'Agendamento',     desc: 'Cliente agenda direto pelo seu perfil público ou você marca pra ele em "Clientes → Agendar sessão". Você define seus horários, modalidade (online, presencial ou híbrido) e o valor da sessão.' },
  { num: '2', title: 'Pagamento',       desc: 'Cliente paga por cartão ou PIX em segundos. Suporta sessão avulsa, recorrência semanal ou mensal, ou lote único antecipado. Confirmação automática.' },
  { num: '3', title: 'Confirmação',     desc: 'Sessão vira "Confirmada" no mesmo instante. Evento aparece no Google Calendar de vocês dois com link do Meet já gerado. Lembrete por WhatsApp pro cliente às 9h do dia anterior.' },
  { num: '4', title: 'Sessão acontece', desc: 'Online via Google Meet integrado, ou presencial onde você definir. Transcrição automática opcional, com consentimento de ambos os lados.' },
  { num: '5', title: 'Fechamento',      desc: 'Dia 23 de cada mês o ciclo fecha automaticamente, agregando todas as sessões realizadas no período (24 do mês anterior → 23 do atual). Sistema calcula sua receita líquida.' },
  { num: '6', title: 'Nota fiscal',     desc: 'Você emite sua nota e faz upload em PDF pelo painel até o dia 28. Equipe valida em até 5 dias úteis. Tomador da NF: Nosso Ello.' },
  { num: '7', title: 'Repasse',         desc: 'Dia 1 do mês seguinte, repasse via PIX direto pra sua chave cadastrada. Gratuito, sem taxa de saída. Histórico completo disponível no painel.' },
];

function ParaTerapeutasPage() {
  useRouteMeta({
    title: 'Para terapeutas e psicólogos — monte seu consultório online',
    description: 'Agenda, pagamentos, prontuário digital e repasse automático. Comece gratuitamente, 3 primeiros meses sem comissão. Sem fidelidade, sem mensalidade no plano Gratuito.',
    canonical: 'https://nossoello.com.br/para-terapeutas',
  });
  return (
    <div className="bg-[#FBF8F3] min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1">
        {/* ─── HERO ─── */}
        <section className="max-w-4xl mx-auto px-6 pt-16 pb-12 text-center">
          <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-100 rounded-full px-4 py-1.5 text-teal-700 text-sm font-medium mb-6">
            Para terapeutas e psicólogos
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-800 leading-tight tracking-tight">
            Toda a sua prática em <span className="text-teal-600">um só lugar</span>
          </h1>
          <p className="text-lg text-slate-500 mt-5 max-w-2xl mx-auto leading-relaxed">
            Agendamento, Google Meet, prontuário, transcrição automática, pagamento e repasse —
            tudo integrado. Você foca no que importa: o cliente.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/onboarding/therapist">
              <button className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-lg font-medium shadow-sm transition">
                Começar gratuitamente
              </button>
            </Link>
            <a href="#planos" className="text-teal-700 hover:text-teal-800 px-6 py-3 rounded-lg font-medium border border-teal-200 hover:bg-teal-50 transition">
              Ver planos e taxas
            </a>
          </div>
        </section>

        {/* ─── DIFERENCIAIS ─── */}
        <section className="max-w-5xl mx-auto px-6 pb-16">
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-800 text-center mb-3">
            O que torna a Nosso Ello diferente
          </h2>
          <p className="text-slate-500 text-center max-w-2xl mx-auto mb-10">
            Não é só uma agenda. É a infraestrutura completa pra você atender clientes
            online ou presencial, sem juntar 5 ferramentas separadas.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <Feature icon="📅" title="Google Calendar + Meet automático" desc="Cada sessão online cria um evento na sua agenda Google com link do Meet único. Cliente recebe convite no calendário dele também." />
            <Feature icon="🤝" title="Cliente agenda sozinho — ou você agenda" desc="Perfil público com a sua disponibilidade real. O cliente escolhe o horário, ou você cria sessões direto pra ele." />
            <Feature icon="🔁" title="Recorrências e lotes" desc="Sessões avulsas, semanais ou mensais. Cobrança por sessão (PER_SESSION) ou lote único antecipado (UPFRONT_BATCH)." />
            <Feature icon="💳" title="Pagamento flexível" desc="Cartão e PIX integrados. Cliente paga em minutos, você recebe automaticamente." />
            <Feature icon="📋" title="Prontuário digital" desc="Queixa principal, evolução, plano terapêutico. Privado, criptografado e disponível só pra você (S-11, LGPD)." />
            <Feature icon="🎙️" title="Transcrição automática" desc="Opcional, com consentimento explícito do terapeuta E do cliente (LGPD). Online via Google Meet ou presencial pelo celular." />
            <Feature icon="💬" title="Confirmação por WhatsApp" desc="Lembrete automático às 9h do dia anterior, com botões pra confirmar ou reagendar (magic-link). Reduz no-show." />
            <Feature icon="✅" title="Validação automática de presença" desc="Sistema confere via Meet API se vocês 2 entraram e ficaram pelo menos 80% do tempo. Sem validação manual." />
            <Feature icon="📥" title="Importe sua carteira atual" desc='Tem clientes que já te pagam por fora? Marque "Já paga fora da plataforma" e use o sistema só pra agendar/Meet/prontuário. Janela de 30 dias pra migração.' />
          </div>
        </section>

        {/* ─── FLUXOGRAMA ─── */}
        <section className="max-w-5xl mx-auto px-6 pb-16">
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-800 text-center mb-3">
            Do agendamento ao repasse
          </h2>
          <p className="text-slate-500 text-center max-w-2xl mx-auto mb-10">
            Tudo automatizado. Você só precisa atender a sessão e preencher o prontuário —
            o resto a plataforma faz sozinha.
          </p>
          <div className="space-y-3">
            {FLOW_STEPS.map((s, i) => (
              <div key={s.num} className="flex gap-4 items-start">
                <div className="flex flex-col items-center shrink-0">
                  <div className="size-12 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 text-white font-bold flex items-center justify-center text-lg shadow-sm">
                    {s.num}
                  </div>
                  {i < FLOW_STEPS.length - 1 && (
                    <div className="w-px h-10 bg-gradient-to-b from-teal-300 to-teal-100 mt-1" />
                  )}
                </div>
                <div className="flex-1 pb-3">
                  <h3 className="font-bold text-slate-800 text-lg">{s.title}</h3>
                  <p className="text-slate-600 text-base mt-1.5 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ─── CICLO DE FATURAMENTO ─── */}
        <section className="max-w-5xl mx-auto px-6 pb-16">
          <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-10">
            <h2 className="text-2xl md:text-3xl font-extrabold text-slate-800 mb-2">
              Ciclo de faturamento mensal
            </h2>
            <p className="text-slate-500 mb-8">
              Previsível, automatizado e transparente. Você acompanha cada etapa no painel.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <CycleCard num="24" label="Início do ciclo" desc="Início da contagem mensal. Cada sessão realizada entra automaticamente no ciclo." />
              <CycleCard num="23" label="Fechamento" desc="Fim do mês contábil. Ciclo agrega todas as sessões realizadas no período." />
              <CycleCard num="28" label="Prazo da NF" desc="Você envia a nota em PDF pelo painel. Equipe valida em até 5 dias úteis." />
              <CycleCard num="01" label="Repasse PIX" desc="Pagamento via PIX direto pra sua chave cadastrada. Gratuito, sem taxa de saída." />
            </div>
            <div className="mt-8 pt-6 border-t border-slate-100 grid sm:grid-cols-3 gap-6">
              <Highlight label="Tomador da NF" value="Nosso Ello (a plataforma)" />
              <Highlight label="Repasse PIX" value="Sem taxa de saída" />
              <Highlight label="Acompanhamento" value="Painel completo em tempo real" />
            </div>
          </div>
        </section>

        {/* ─── PLANOS ─── */}
        <section id="planos" className="max-w-6xl mx-auto px-6 pb-16">
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-800 text-center mb-3">
            Planos e taxas
          </h2>
          <p className="text-slate-500 text-center max-w-2xl mx-auto mb-6">
            Sem mensalidade no plano Gratuito. Só paga quando crescer — e a taxa cai quanto maior o plano.
          </p>

          <div className="max-w-2xl mx-auto mb-10 rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-amber-100 p-5 flex items-start gap-4">
            <div className="text-3xl shrink-0">🎁</div>
            <div className="flex-1 text-left">
              <h3 className="text-base font-bold text-amber-900">Promoção de adesão — 3 meses grátis</h3>
              <p className="text-sm text-amber-800 mt-1 leading-relaxed">
                Criou perfil agora? Nos <strong>primeiros 90 dias</strong>, a plataforma <strong>zera a
                comissão</strong> de todas as suas sessões — você recebe <strong>100% do que o cliente pagar</strong>,
                independente do plano. Depois disso, segue a tabela abaixo.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {PLANS.map((p) => <PlanCard key={p.tier} {...p} />)}
          </div>
          <p className="text-xs text-slate-400 text-center mt-8 max-w-3xl mx-auto">
            Taxa cobrada sobre o valor da sessão, descontada no repasse mensal. Mudança de plano vigora
            no fechamento do ciclo (sessões já concluídas mantêm a taxa do plano em que foram realizadas).
          </p>
        </section>

        {/* ─── CTA FINAL ─── */}
        <section className="max-w-5xl mx-auto px-6 pb-20">
          <div className="bg-gradient-to-r from-teal-500 to-coral-500 rounded-[28px] p-8 md:p-12 text-white text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold mb-3">Pronto pra começar?</h2>
            <p className="text-white/90 text-base max-w-xl mx-auto leading-relaxed mb-8">
              Cadastro em poucos minutos. Comece no plano Gratuito e migre quando quiser.
              Sem fidelidade, sem multa.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/onboarding/therapist">
                <button className="bg-white text-teal-700 hover:bg-teal-50 px-6 py-3 rounded-lg font-medium shadow-sm transition">
                  Criar minha conta
                </button>
              </Link>
              <Link to="/como-funciona">
                <button className="bg-white/15 hover:bg-white/25 text-white px-6 py-3 rounded-lg font-medium border border-white/30 transition">
                  Como funciona pro cliente
                </button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}

function Feature({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 hover:border-teal-200 hover:shadow-sm transition">
      <div className="text-3xl mb-3">{icon}</div>
      <h3 className="font-bold text-slate-800 text-base">{title}</h3>
      <p className="text-slate-600 text-sm mt-2 leading-relaxed">{desc}</p>
    </div>
  );
}

function CycleCard({ num, label, desc }: { num: string; label: string; desc: string }) {
  return (
    <div className="bg-teal-50/50 border border-teal-100 rounded-xl p-5 text-center">
      <div className="text-4xl font-extrabold text-teal-600">{num}</div>
      <div className="text-xs font-bold text-teal-700 uppercase tracking-wide mt-1.5">{label}</div>
      <p className="text-sm text-slate-600 mt-2.5 leading-relaxed">{desc}</p>
    </div>
  );
}

function Highlight({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider font-bold text-slate-400">{label}</div>
      <div className="text-base font-semibold text-slate-700 mt-1">{value}</div>
    </div>
  );
}

function PlanCard({ tier, name, price, feePct, highlights, badge, accent }: any) {
  const isHighlighted = accent === 'teal';
  return (
    <div className={
      'rounded-2xl p-6 border-2 flex flex-col ' +
      (isHighlighted
        ? 'border-teal-400 bg-white shadow-lg scale-105'
        : 'border-slate-200 bg-white')
    }>
      {badge && (
        <div className={
          'self-start text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full mb-3 ' +
          (accent === 'teal' ? 'bg-teal-500 text-white' : accent === 'coral' ? 'bg-coral-500 text-white' : 'bg-slate-100 text-slate-600')
        }>
          {badge}
        </div>
      )}
      <h3 className="text-2xl font-extrabold text-slate-800">{name}</h3>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="text-4xl font-extrabold text-slate-900">{price}</span>
        <span className="text-base text-slate-500">/mês</span>
      </div>
      <div className="mt-2 text-base text-slate-600">Taxa por sessão: <strong className="text-slate-800">{feePct}%</strong></div>
      <ul className="space-y-2.5 mt-6 flex-1">
        {highlights.map((h: string, i: number) => (
          <li key={i} className="flex items-start gap-2 text-base text-slate-700">
            <span className="text-teal-500 shrink-0 mt-1">✓</span>
            <span>{h}</span>
          </li>
        ))}
      </ul>
      <Link to="/onboarding/therapist" className="mt-6 block">
        <button className={
          'w-full py-3 rounded-lg font-medium text-base transition ' +
          (isHighlighted
            ? 'bg-teal-500 hover:bg-teal-600 text-white shadow-sm'
            : 'border border-slate-300 hover:border-teal-400 hover:text-teal-700 text-slate-700')
        }>
          Começar com {name}
        </button>
      </Link>
    </div>
  );
}
